import { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import {
  FormField,
  ActionButton,
  TableSection,
} from '@shared/components/add-form/add-form.component';

// ============================================================================
// INTERFACES
// ============================================================================

export interface LogicTransferFormConfig {
  title: string;
  formFields: FormField[];
  headerActions: ActionButton[];
  tableSections?: TableSection[];
  listRoute: string;
}

// ============================================================================
// TRANSFER TYPE OPTIONS
// ============================================================================

export const TRANSFER_TYPE_OPTIONS = [
  {
    label: 'logic_transfer.form.transfer_type.main_to_store',
    value: 'MAIN_TO_STORE',
  },
  {
    label: 'logic_transfer.form.transfer_type.store_to_store',
    value: 'STORE_TO_STORE',
  },
];

export interface LogicTransferItem {
  id?: string;
  serialNumber?: number;
  transferType: string; // Transfer type
  code: string; // Transfer order number
  // Warehouse info from API (IDs)
  supportLogicWarehouseId?: string | number;
  receivingLogicWarehouseId?: string | number;
  // Warehouse info for UI display (code/name)
  supportWarehouseCode?: string;
  supportWarehouseName?: string;
  receiveWarehouseCode?: string;
  receiveWarehouseName?: string;
  // Dates
  expectedReceiveDate?: string;
  plannedDeliveryDate?: string;
  creationDate?: string;
  lastUpdateDate?: string;
  // Items
  items?: LogicTransferItemDetail[];
  description?: string;
  status: string;
  createdBy?: string;
  lastUpdatedBy?: string;
  transferTypeName?: string;
}

export interface LogicTransferDetail {
  id?: string;
  spuCode: string;
  spuName: string;
  skuCode: string;
  skuName: string;
  quantity: number; // Transfer quantity
  batchId?: string;
  /** True when serial profile requires a lot for this SKU. */
  lotRequired?: boolean;
  fefoSuggestedBatchId?: string;
  overrideReason?: string;
  stockNum?: number; // Current stock quantity
  outStockNum?: number; // Source stock quantity
  outStockUsableNum?: number; // Available transfer quantity
  inStockNum?: number; // Destination stock quantity
  inStockUsableNum?: number; // Destination available stock
  planNum?: number; // Plan transfer quantity (deprecated, use quantity)
}

// New interface matching API structure: items: [{SKU code, plannedQty}]
export interface LogicTransferItemDetail {
  id?: string;
  skuCode: string; // SKU code
  plannedQty: number; // Planned quantity
  transferOrderId: string;
  creationDate: string;
}

export interface LogicTransferResponse {
  data: LogicTransferItem[];
  totalRecords: number;
  loading: boolean;
}

export interface LogicTransferAction {
  action: string;
  label: string;
  icon: string;
  severity: 'primary' | 'secondary' | 'success' | 'info' | 'danger';
  outlined?: boolean;
  text?: boolean;
  disabled?: boolean;
  visible?: boolean;
}

// ============================================================================
// FORM FIELD OPTIONS
// ============================================================================

export const LOGIC_TRANSFER_STATUS_OPTIONS = [
  { value: 'PENDING', label: 'logic_transfer.status.pending', orderSeq: 1 },
  { value: 'APPROVED', label: 'logic_transfer.status.approved', orderSeq: 2 },
  {
    value: 'IN_PROGRESS',
    label: 'logic_transfer.status.in_progress',
    orderSeq: 3,
  },
  { value: 'COMPLETED', label: 'logic_transfer.status.completed', orderSeq: 4 },
  { value: 'CANCELLED', label: 'logic_transfer.status.cancelled', orderSeq: 5 },
];

// ============================================================================
// SEARCH FORM CONFIGURATIONS
// ============================================================================

export const LOGIC_TRANSFER_CONFIG: FormFieldConfig[] = [
  {
    key: 'code',
    label: 'logic_transfer.search_filter.transfer_order_number',
    type: 'text',
    placeholder: 'logic_transfer.form.placeholders.enter_transfer_order_number',
  },
  {
    key: 'transferType',
    label: 'logic_transfer.search_filter.transfer_type',
    type: 'select',
    options: TRANSFER_TYPE_OPTIONS,
    placeholder: 'logic_transfer.form.placeholders.select_transfer_type',
  },
  {
    key: 'status',
    label: 'logic_transfer.search_filter.status',
    type: 'select',
    options: LOGIC_TRANSFER_STATUS_OPTIONS,
    placeholder: 'logic_transfer.form.placeholders.select_status',
  },
  {
    key: 'supportWarehouseIdList',
    label: 'logic_transfer.search_filter.source_logic_warehouse',
    type: 'searchableDropdown',
    options: [],
    showClear: true,
    filter: true,
  },
  {
    key: 'receiveWarehouseIdList',
    label: 'logic_transfer.search_filter.destination_logic_warehouse',
    type: 'searchableDropdown',
    options: [],
    filter: true,
    showClear: true,
  },
  {
    key: 'creationDate',
    label: 'logic_transfer.table.columns.creation_time',
    type: 'dateRange',
    range: { startKey: 'beginCreationDate', endKey: 'endCreationDate' },
  },
];

// ============================================================================
// STATUS FORMATTING
// ============================================================================

export const formatLogicTransferStatus = (status: string): string => {
  const statusMap: { [key: string]: string } = {
    PENDING: 'logic_transfer.status.pending',
    APPROVED: 'logic_transfer.status.approved',
    IN_PROGRESS: 'logic_transfer.status.in_progress',
    COMPLETED: 'logic_transfer.status.completed',
    CANCELLED: 'logic_transfer.status.cancelled',
  };

  return statusMap[status] || status;
};

export const getLogicTransferStatusSeverity = (status: string): string => {
  const severityMap: { [key: string]: string } = {
    PENDING: 'warning',
    APPROVED: 'success',
    IN_PROGRESS: 'info',
    COMPLETED: 'success',
    CANCELLED: 'secondary',
  };

  return severityMap[status] || 'info';
};

// ============================================================================
// TABLE COLUMN CONFIGURATIONS
// ============================================================================

/**
 * Default column property names for Logic Transfer table
 */
export const LOGIC_TRANSFER_DEFAULT_COLUMNS = {
  CODE: 'code',
  TRANSFER_TYPE_NAME: 'transferTypeName',
  SUPPORT_WAREHOUSE_NAME: 'supportWarehouseName',
  RECEIVE_WAREHOUSE_NAME: 'receiveWarehouseName',
  DESCRIPTION: 'description',
  STATUS: 'status',
  EXPECTED_RECEIVE_DATE: 'expectedReceiveDate',
  CREATION_DATE: 'creationDate',
} as const;

/**
 * Flexible column property names for Logic Transfer table
 */
export const LOGIC_TRANSFER_FLEXIBLE_COLUMNS = {
  // No flexible columns
} as const;

/**
 * Action column property name for Logic Transfer table
 */
export const LOGIC_TRANSFER_ACTION_COLUMN = {
  ACTIONS: 'actions',
} as const;

/**
 * Default columns order for Logic Transfer table
 */
export const LOGIC_TRANSFER_DEFAULT_COLUMNS_ORDER: string[] = [
  LOGIC_TRANSFER_DEFAULT_COLUMNS.CODE,
  LOGIC_TRANSFER_DEFAULT_COLUMNS.TRANSFER_TYPE_NAME,
  LOGIC_TRANSFER_DEFAULT_COLUMNS.SUPPORT_WAREHOUSE_NAME,
  LOGIC_TRANSFER_DEFAULT_COLUMNS.RECEIVE_WAREHOUSE_NAME,
  LOGIC_TRANSFER_DEFAULT_COLUMNS.DESCRIPTION,
  LOGIC_TRANSFER_DEFAULT_COLUMNS.STATUS,
  LOGIC_TRANSFER_DEFAULT_COLUMNS.EXPECTED_RECEIVE_DATE,
  LOGIC_TRANSFER_DEFAULT_COLUMNS.CREATION_DATE,
];

/**
 * Flexible columns array for Logic Transfer table
 */
export const LOGIC_TRANSFER_FLEXIBLE_COLUMNS_ORDER: string[] = [];

export const LOGIC_TRANSFER_TABLE_COLUMNS: TableColumn[] = [
  {
    label: 'logic_transfer.table.columns.transfer_order_number',
    prop: LOGIC_TRANSFER_DEFAULT_COLUMNS.CODE,
    width: 180,
  },
  {
    label: 'logic_transfer.table.columns.transfer_type',
    prop: LOGIC_TRANSFER_DEFAULT_COLUMNS.TRANSFER_TYPE_NAME,
    width: 180,
  },
  {
    label: 'logic_transfer.table.columns.source_logic_warehouse',
    prop: LOGIC_TRANSFER_DEFAULT_COLUMNS.SUPPORT_WAREHOUSE_NAME,
    width: 200,
  },
  {
    label: 'logic_transfer.table.columns.destination_logic_warehouse',
    prop: LOGIC_TRANSFER_DEFAULT_COLUMNS.RECEIVE_WAREHOUSE_NAME,
    width: 200,
  },
  {
    label: 'logic_transfer.table.columns.description',
    prop: LOGIC_TRANSFER_DEFAULT_COLUMNS.DESCRIPTION,
    width: 150,
    type: 'text',
    align: 'left',
  },
  {
    label: 'logic_transfer.table.columns.status',
    prop: LOGIC_TRANSFER_DEFAULT_COLUMNS.STATUS,
    width: 200,
    type: 'status',
  },
  {
    label: 'logic_transfer.table.columns.expected_receive_date',
    prop: LOGIC_TRANSFER_DEFAULT_COLUMNS.EXPECTED_RECEIVE_DATE,
    width: 180,
    type: 'custom',
    align: 'center',
  },
  {
    label: 'logic_transfer.table.columns.creation_time',
    prop: LOGIC_TRANSFER_DEFAULT_COLUMNS.CREATION_DATE,
    width: 180,
    type: 'custom',
    align: 'center',
    sortable: true,
  },
  {
    label: 'logic_transfer.table.columns.actions',
    prop: LOGIC_TRANSFER_ACTION_COLUMN.ACTIONS,
    type: 'actions',
    width: 120,
    align: 'center',
  },
];

// ============================================================================
// ACTION CONFIGURATIONS
// ============================================================================

export const getLogicTransferActions = (
  record: LogicTransferItem,
  isVerify: boolean = false
): LogicTransferAction[] => {
  const actions: LogicTransferAction[] = [];
  const status = record.status?.toUpperCase() || record.status;

  // PENDING status: Chỉnh sửa, Phê duyệt, Hủy, Xoá
  if (status === 'PENDING') {
    actions.push({
      action: 'view',
      label: 'common.actions.view',
      icon: 'pi pi-eye',
      severity: 'info',
      text: true,
    });

    actions.push({
      action: 'edit',
      label: 'common.actions.edit',
      icon: 'pi pi-pencil',
      severity: 'secondary',
      text: true,
    });

    actions.push({
      action: 'approve',
      label: 'common.actions.approve',
      icon: 'pi pi-check',
      severity: 'info',
      text: true,
    });

    actions.push({
      action: 'cancel',
      label: 'common.actions.cancel',
      icon: 'pi pi-ban',
      severity: 'secondary',
      text: true,
    });
  }
  // APPROVED status: Xem chi tiết, Tạo Phiếu Xuất kho (ODO), Xoá
  else if (status === 'APPROVED') {
    actions.push({
      action: 'view',
      label: 'common.actions.view',
      icon: 'pi pi-eye',
      severity: 'info',
      text: true,
    });

    actions.push({
      action: 'createODO',
      label: 'logic_transfer.actions.create_outbound_delivery',
      icon: 'pi pi-file-export',
      severity: 'info',
      text: true,
    });

    actions.push({
      action: 'cancel',
      label: 'common.actions.cancel',
      icon: 'pi pi-ban',
      severity: 'secondary',
      text: true,
    });
  }
  // CANCELLED status: Chỉ xem chi tiết
  else if (status === 'CANCELLED') {
    actions.push({
      action: 'view',
      label: 'common.actions.view',
      icon: 'pi pi-eye',
      severity: 'info',
      text: true,
    });
  }

  return actions;
};

// ============================================================================
// FORM FIELD CONFIGURATIONS FOR ADD/EDIT
// ============================================================================

export const LOGIC_TRANSFER_FORM_FIELDS: FormField[] = [
  {
    label: 'logic_transfer.form.fields.transfer_order_number',
    controlName: 'code',
    type: 'text',
    placeholder: 'logic_transfer.form.placeholders.enter_transfer_order_number',
    required: true,
    disabled: false,
    maxLength: 255,
    section: 'logic_transfer.form.sections.basic_information',
  },
  {
    label: 'logic_transfer.form.fields.transfer_type',
    controlName: 'transferType',
    type: 'dropdown',
    placeholder: 'logic_transfer.form.placeholders.select_transfer_type',
    required: true,
    section: 'logic_transfer.form.sections.basic_information',
    options: TRANSFER_TYPE_OPTIONS,
  },
  {
    label: 'logic_transfer.form.fields.description',
    controlName: 'description',
    type: 'text',
    placeholder: 'logic_transfer.form.placeholders.enter_description',
    required: false,
    section: 'logic_transfer.form.sections.basic_information',
  },
  {
    label: 'logic_transfer.form.fields.source_warehouse',
    controlName: 'supportWarehouseCode',
    type: 'searchableDropdown',
    placeholder: 'logic_transfer.form.placeholders.select_source_warehouse',
    required: true,
    section: 'logic_transfer.form.sections.warehouse_information',
    options: [], // Will be populated dynamically
  },
  {
    label: 'logic_transfer.form.fields.destination_warehouse',
    controlName: 'receiveWarehouseCode',
    type: 'searchableDropdown',
    placeholder:
      'logic_transfer.form.placeholders.select_destination_warehouse',
    required: true,
    section: 'logic_transfer.form.sections.warehouse_information',
    options: [], // Will be populated dynamically
  },
  {
    label: 'logic_transfer.form.fields.expected_receive_date',
    controlName: 'expectedReceiveDate',
    type: 'date',
    placeholder:
      'logic_transfer.form.placeholders.select_expected_receive_date',
    required: true,
    section: 'logic_transfer.form.sections.warehouse_information',
    minDate: new Date(),
  },
];

// ============================================================================
// TABLE SECTION CONFIGURATIONS
// ============================================================================

export const LOGIC_TRANSFER_TABLE_SECTIONS: TableSection[] = [
  {
    title: 'logic_transfer.form.sections.product_details',
    show: true,
    hasCheckbox: true,
    hasSerialColumn: true,
    data: [],
    columns: [
      {
        prop: 'spuCode',
        label: 'logic_transfer.form.table.columns.spu_code',
        width: 150,
        type: 'text',
        align: 'left',
      },
      {
        prop: 'spuName',
        label: 'logic_transfer.form.table.columns.spu_name',
        width: 200,
        type: 'text',
        align: 'left',
      },
      {
        prop: 'skuCode',
        label: 'logic_transfer.form.table.columns.sku_code',
        width: 150,
        type: 'text',
        align: 'left',
      },
      {
        prop: 'skuName',
        label: 'logic_transfer.form.table.columns.sku_name',
        width: 200,
        type: 'text',
        align: 'left',
      },
      {
        prop: 'stockNum',
        label: 'logic_transfer.form.table.columns.stock_quantity',
        width: 150,
        type: 'text',
        align: 'right',
        useGrouping: true,
      },
      {
        prop: 'batchId',
        label: 'inbound.fields.lot',
        width: 180,
        type: 'inputText',
        align: 'left',
      },
      {
        prop: 'quantity',
        label: 'logic_transfer.form.table.columns.quantity',
        width: 150,
        type: 'inputNumber',
        align: 'left',
        min: 1,
        useGrouping: true,
      },
      {
        prop: 'actions',
        label: 'logic_transfer.form.table.columns.actions',
        width: 100,
        type: 'actions',
        align: 'center',
      },
    ],
    actions: [
      {
        label: 'logic_transfer.form.table.actions.delete_products',
        action: 'deleteSelected',
        severity: 'danger',
        outlined: true,
        disabled: true,
        icon: 'delete',
      },
      {
        label: 'logic_transfer.form.table.actions.add_products',
        action: 'selectProducts',
        severity: 'info',
        icon: 'add',
      },
    ],
  },
];

// ============================================================================
// HEADER ACTIONS
// ============================================================================

export const LOGIC_TRANSFER_HEADER_ACTIONS: ActionButton[] = [
  {
    label: 'common.actions.cancel',
    action: 'cancel',
    outlined: true,
    severity: 'secondary',
  },
  {
    label: 'common.actions.save',
    action: 'save',
    severity: 'info',
  },
];

// View mode header actions (only back button)
export const LOGIC_TRANSFER_HEADER_ACTIONS_VIEW: ActionButton[] = [
  {
    label: 'common.actions.back',
    action: 'cancel',
    outlined: true,
    severity: 'secondary',
  },
];
